# Application methods

When posting a job ad you must specify if candidates apply via [SEEK’s Apply Form](#seeks-apply-form),
or the hirer’s apply form by [linking out](#linking-out-to-an-external-apply-form).
SEEK’s Apply Form is strongly recommended for the best candidate experience and conversion rates.

We do not recommend changing the application method of a job ad while it is live on the SEEK platform.
Attempting to switch a live job ad between SEEK’s Apply Form and an external apply form may result in unexpected behaviour and complicate applicant tracking.
The resulting candidate records may differ in their level of detail.
To accommodate the hirer need, you may support closing and re-posting the job ad to SEEK with a different application method.

## SEEK’s Apply Form

Candidates apply via the SEEK website and app.
This allows a candidate to use documents and information already stored in their SEEK Profile.
The job ad may specify a [questionnaire][questionnaires] for candidates to complete as part of their job application.
Questionnaires are rendered on SEEK’s candidate website and in our mobile apps.

You should use [Optimised Apply] to retrieve applications submitted on SEEK.
Optimised Apply is compatible with questionnaires.
While hirers can also view applicants on the [SEEK Employer website](https://employer.seek.com), that may not be part of their typical workflow.
You can validate that you have an [`ApplicationExport` relationship] with the SEEK hirer before posting a job ad that uses SEEK’s Apply Form.

SEEK’s Apply Form is used when no additional `applicationMethods` are provided in [`PostingInstruction`]:

```jsonc
{
  // Default to SEEK’s Apply Form
  "applicationMethods": null

  // Other posting instruction fields
}
```

## Linking out to an external apply form

Candidates apply via an external careers website that is hosted by your recruitment software.
[Apply with SEEK] can be implemented to allow candidates to pre-fill your external apply form with their SEEK Profile.

SEEK hirers are not configured to link out by default.
If a hirer needs to link out,
they need to contact SEEK and comply with our link-out terms of use.

The [Developer Dashboard hirers page] displays the SEEK hirers that you have a relationship with and lets you check whether they have the required `ApplicationUri` capability.
You can also use the [`hiringOrganization` query] or [`self` query] to achieve the same effect:

```graphql
query ($id: String!) {
  hiringOrganization(id: $id) {
    seekApiCapabilities {
      applicationMethodCodes
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:organization:seek:93WyyF1h"
}
```

```json
{
  "hiringOrganization": {
    "seekApiCapabilities": {
      "applicationMethodCodes": ["ApplicationUri"]
    }
  }
}
```

You can link out to the hirer’s apply form by providing an `applicationUri` on the job ad’s [`ApplicationMethodInput`].
This replaces SEEK’s Apply Form with an external apply form,
so [Optimised Apply] and [questionnaires] are not available.

A link-out [`PostingInstruction`] looks something like this:

```jsonc
{
  // Set explicitly to an external apply form
  "applicationMethods": [
    {
      "applicationUri": {
        "url": "https://example.com/position/123/apply"
      }
    }
  ]

  // Other posting instruction fields
}
```

If you try to provide an external apply form for a hirer without the required capability,
the posting mutation will fail with a permission error:

```json
{
  "errors": [
    {
      "message": "Invalid application URL: Application linkout not permitted",
      "extensions": {
        "code": "BAD_USER_INPUT",
        "invalidFields": {
          "/input/positionProfile/postingInstructions/0/applicationMethods/0/applicationUri/url": "Application linkout not permitted"
        }
      }
    }
  ],
  "data": null
}
```

The SEEK API implicitly trusts your software to provide a safe `applicationUri`.
This avoids the maintenance burden of strict URL allowlists.

Follow best practices around URL redirection to protect candidates from phishing attempts.
At a minimum,
your software must have server-side provisions to ensure the `applicationUri` points to a web resource that your organization manages,
and prevent bad actors from manipulating redirection behaviour.

See the [OWASP cheat sheet] for best practices,
and our [Terms of Use] for more information about your obligations.

[`ApplicationExport` relationship]: /auth/hirer-relationships
[`ApplicationMethodInput`]: /schema/named-type/ApplicationMethodInput
[`hiringOrganization` query]: /schema/query/hiringOrganization
[`PostingInstruction`]: /schema/named-type/PostingInstruction
[`self` query]: /schema/query/self
[Apply with SEEK]: /use-cases/apply-with-seek
[Developer Dashboard hirers page]: /manage/hirers
[Optimised Apply]: /use-cases/application-export
[OWASP cheat sheet]: https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html
[questionnaires]: /use-cases/job-posting/questionnaires
[Terms of Use]: https://au.employer.seek.com/partners/terms-of-use
